import React, { useState, useEffect } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, ArrowLeft, Medal, Star } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANK_STYLES = {
  1: { bg: 'linear-gradient(135deg, #FFD700, #FFA000)', text: '#000', icon: '🥇' },
  2: { bg: 'linear-gradient(135deg, #C0C0C0, #9E9E9E)', text: '#000', icon: '🥈' },
  3: { bg: 'linear-gradient(135deg, #CD7F32, #A0522D)', text: '#FFF', icon: '🥉' },
};

const LeaderboardPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        setEvent(r.data);
        const lb = await axios.get(`${API}/events/${r.data.event_id}/leaderboard`);
        setScores(lb.data);
      } catch {}
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent" /></div>;

  return (
    <div className="min-h-screen px-5 py-8" style={{ background: 'radial-gradient(circle at top, #1a1510, #15161B)', fontFamily: 'Inter, sans-serif' }} data-testid="leaderboard-page">
      <ConfettiBackground />
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <BackButton fallbackHref={`/event/${slug}/guest`} label="Back" variant="subtle" testId="leaderboard-back" />
        <h1 className="text-xl font-black text-white flex items-center gap-2"><Trophy size={22} weight="fill" className="text-amber-400" /> Leaderboard</h1>
      </div>

      {event && <p className="text-white/30 text-sm mb-6">{event.event_name}</p>}

      {scores.length === 0 ? (
        <div className="text-center py-20">
          <Trophy size={48} weight="bold" className="text-white/10 mx-auto mb-4" />
          <p className="text-white/30 text-base">No scores yet</p>
          <p className="text-white/15 text-sm mt-1">Upload moments to start climbing!</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="leaderboard-list">
          {scores.map((s, i) => {
            const rs = RANK_STYLES[s.rank];
            const isTop3 = s.rank <= 3;
            return (
              <div key={s.guest_id}
                className={`flex items-center gap-4 px-5 py-4 rounded-2xl transition-all ${isTop3 ? '' : ''}`}
                style={{
                  background: isTop3 ? rs.bg : 'rgba(255,255,255,0.04)',
                  border: isTop3 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                  color: isTop3 ? rs.text : '#FFF',
                }}
                data-testid={`rank-${s.rank}`}>
                {/* Rank */}
                <div className="w-8 text-center flex-shrink-0">
                  {isTop3 ? (
                    <span className="text-xl">{rs.icon}</span>
                  ) : (
                    <span className="text-white/30 font-bold text-sm">{s.rank}</span>
                  )}
                </div>

                {/* Name + badge */}
                <div className="flex-1 min-w-0">
                  <p className={`font-bold text-base truncate ${isTop3 ? '' : 'text-white'}`}>{s.name}</p>
                  {s.badge && (
                    <span className={`text-xs font-semibold ${isTop3 ? 'opacity-70' : 'text-amber-400'}`}>{s.badge}</span>
                  )}
                  {!isTop3 && s.gap_to_next > 0 && (
                    <span className="text-xs text-[#B89762] font-semibold ml-2">{s.gap_to_next} pts to #{s.next_rank}</span>
                  )}
                </div>

                {/* Stats */}
                <div className="text-right flex-shrink-0">
                  <p className={`text-lg font-black ${isTop3 ? '' : 'text-white'}`}>{s.score} pts</p>
                  <p className={`text-xs ${isTop3 ? 'opacity-60' : 'text-white/25'}`}>{s.uploads} uploads · {s.challenges} challenges</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Scoring info */}
      <div className="mt-8 rounded-2xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-white/30 text-sm font-semibold mb-2">How scoring works</p>
        <div className="flex justify-center gap-6 text-xs text-white/20">
          <span>Upload = 1 pt</span>
          <span>Challenge = 3 pts</span>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
